import React, { ReactNode, useMemo } from 'react';
import { AvTooltip } from '@fabric-ui/components';
import { Delete, Edit, Plus } from '@fabric-ui/icons';
import { flex } from '@fabric-ui/theme';
import { noop , iconSize } from '@fabric-ui/utils';
import { Button } from '@mui/material';
import Box from '@mui/material/Box';
import { Link } from '../../utils/AvRouter';
import DeleteButtonWithDialog from './DeleteButtonWithDialog';
import { ToolbarActionButton } from './table.utils';
import { DeleteProps } from './types';

const plusIcon = <Plus style={{ marginLeft: '-6px' }} />;
const deleteIcon = (size?) => <Delete style={iconSize(size)} />;
const editIcon = (size?) => <Edit style={iconSize(size)} />;

const NO_PERMISSION_MESSAGE = 'You need permissions to perform this action';

interface TableToolbarProps {
  keyName: string;
  rows: Array<Record<string, any>>;
  setRows?: (rows: Array<Record<string, any>>) => void;
  selected: Array<number | string>;
  deleteProps?: DeleteProps;
  customActions: ((selected, selectedRows, setRows, handleSelected) => ReactNode)[];
  customGlobalActions?: Array<() => ReactNode>;
  allowEdit: boolean;
  allowCreate: boolean;
  activeKey?: string | number;
  customNewButton?: ReactNode;
  innerEditLink?: (v) => string | { to: string; search?: any; state?: any };
  handleSelected?: (v) => void;
}

const TableToolbar: React.FC<TableToolbarProps> = ({
  keyName,
  rows,
  setRows = noop,
  selected,
  deleteProps,
  customActions,
  customGlobalActions = [],
  allowEdit,
  allowCreate,
  activeKey,
  customNewButton,
  innerEditLink,
  handleSelected = noop,
}) => {
  const selectedCount = selected.length;
  const getRow = selectedId => rows.find(({ [keyName]: id }) => id === selectedId);
  const selectedRows = useMemo(() => selected.map(id => getRow(id)), [selected, rows]);
  const getSelectedRowName = selectedId => deleteProps?.getName?.(getRow(selectedId));
  const message =
    rows.length && deleteProps?.customMessage
      ? deleteProps.customMessage(selectedRows)
      : deleteProps?.getName
        ? selected.map(selectedId => getSelectedRowName(selectedId)).join(', ')
        : '';
  const deleteButtonFunc = onClick =>
    ToolbarActionButton(
      `Delete${selectedCount > 1 ? ` (${selectedCount})` : ''}`,
      onClick,
      deleteIcon(),
      selectedCount === 0 || !deleteProps?.onDelete || (deleteProps?.disableMultipleDelete && selectedCount > 1)
    );
  return (
    <Box sx={{ ...flex.row, marginBottom: '24px' }}>
      {customActions.map(action => (
        <Box key={action.name}>{action(selected, selectedRows, setRows, handleSelected)}</Box>
      ))}
      {deleteProps && (
        <DeleteButtonWithDialog
          button={deleteButtonFunc}
          selected={selected}
          handleSelected={handleSelected}
          deleteProps={{ ...deleteProps, message }}
        />
      )}
      {allowEdit && (
        <div>
          <Button
            component={Link}
            variant="filter"
            className="transparent"
            to={innerEditLink?.(selected[0]) || `${activeKey ? '../' : ''}edit/${selected[0]}`}
            disabled={selectedCount !== 1}>
            {editIcon()}
            <span>Edit</span>
          </Button>
        </div>
      )}
      <span style={{ flexGrow: 1 }} />
      <Box sx={{ ...flex.itemsCenter, gap: 2 }}>
        {customNewButton ?? (
          <AvTooltip disabled={allowCreate} title={NO_PERMISSION_MESSAGE}>
            <div>
              <Button
                disabled={!allowCreate}
                onClick={e => e.stopPropagation()}
                component={Link}
                to="create/"
                variant="contained"
                size="small">
                {plusIcon}
                <span>Create</span>
              </Button>
            </div>
          </AvTooltip>
        )}
        {customGlobalActions.map(action => (
          <React.Fragment key={action.name}>{action()}</React.Fragment>
        ))}
      </Box>
    </Box>
  );
};

export default TableToolbar;
